import { useState } from 'react';
import { Outlet, Navigate, useNavigate, Link, useLocation } from 'react-router-dom';
import { usePortalAuth } from '@/contexts/PortalAuthContext';
import { Loader2, LogOut, ChevronDown, LayoutDashboard, Briefcase, FileText, Building2 } from 'lucide-react';
import { HelpLink } from '@/components/help/HelpLink';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/portal', icon: LayoutDashboard },
  { label: 'Jobs', path: '/portal/jobs', icon: Briefcase },
  { label: 'Submissions', path: '/portal/submissions', icon: FileText },
];

export function PortalLayout() {
  const { user, isAuthenticated, isLoading, logout } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-secondary">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal/login" state={{ from: location }} replace />;
  }

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  const getInitials = () => {
    const first = user?.firstName?.charAt(0).toUpperCase() || '';
    const last = user?.lastName?.charAt(0).toUpperCase() || '';
    return first + last || '?';
  };

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Top Header Bar */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-bg-secondary border-b border-white/10 flex items-center justify-between px-4 lg:px-8 z-20">
        {/* Left: Logo + Company Name */}
        <div className="flex items-center gap-4">
          <Link to="/portal" className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center text-sm font-bold">
              O
            </div>
            <span className="text-lg font-semibold text-text-primary hidden sm:block">
              Client Portal
            </span>
          </Link>
          {user?.companyName && (
            <>
              <div className="hidden sm:block h-6 w-px bg-white/10" />
              <div className="hidden sm:flex items-center gap-2 text-text-secondary text-sm">
                <Building2 className="h-4 w-4" />
                <span>{user.companyName}</span>
              </div>
            </>
          )}
        </div>

        {/* Center: Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map((item) => {
            const isActive =
              item.path === '/portal'
                ? location.pathname === '/portal'
                : location.pathname.startsWith(item.path);
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-emerald-600/20 text-emerald-400'
                    : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Right: Help + User Menu */}
        <div className="flex items-center gap-2">
          <HelpLink audience="client" />

          <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors outline-none"
          >
            <div className="w-8 h-8 rounded-full bg-emerald-900/50 text-emerald-400 flex items-center justify-center text-sm font-medium">
              {getInitials()}
            </div>
            <span className="text-sm text-text-primary hidden sm:block">
              {user?.firstName} {user?.lastName}
            </span>
            <ChevronDown
              className={`h-4 w-4 text-text-tertiary transition-transform ${menuOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-2 w-48 rounded-lg border border-white/10 bg-bg-elevated shadow-lg z-50">
                <div className="px-4 py-3 border-b border-white/10">
                  <p className="text-sm font-medium text-text-primary">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-text-tertiary truncate">{user?.email}</p>
                </div>
                {/* Mobile nav links */}
                <div className="md:hidden py-1 border-b border-white/10">
                  {NAV_ITEMS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors"
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </Link>
                    );
                  })}
                  <HelpLink
                    audience="client"
                    variant="menu"
                    onClick={() => setMenuOpen(false)}
                  />
                </div>
                <div className="py-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </div>
            </>
          )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
